import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import styles from "./MediaInfo.module.scss";
import ModalRoot from "../_primitives/Modal/ModalRoot/ModalRoot";
import ModalTrigger from "../_primitives/Modal/ModalTrigger/ModalTrigger";
import ModalContent from "../_primitives/Modal/ModalContent/ModalContent";
import { ModalClose } from "../_primitives/Modal/ModalClose/ModalClose";
import { FileType, ImageType } from "@/types/baseTypes";
import ModalPortal from "../_primitives/Modal/ModalPortal/ModalPortal";
import { ReactElement, useRef } from "react";
import ModalOverlay from "../_primitives/Modal/ModalOverlay/ModalOverlay";
import MediaInfoModalContent from "./MediaInfoModalContent";
import { generateCopyrightJSX } from "../Copyright/Copyright";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG";

interface MediaInfoProps {
  media: React.ReactNode;
  image_fullhd?: ImageType | false;
  image_desktop?: ImageType | false;
  image_tablet?: ImageType | false;
  image_mobile?: ImageType | false;
  video?: FileType;
  copyright?: string;
  copyrightAriaLabel?: string;
  trigger?: ReactElement;
  modalInsideBody?: boolean;
  disableAbsolute?: boolean;
}

const MediaInfo = ({
  media,
  video,
  image_desktop,
  image_fullhd,
  image_tablet,
  image_mobile,
  trigger,
  modalInsideBody = false,
  disableAbsolute = false,
  copyright,
  copyrightAriaLabel,
  //images want their modal to be inside the section where they are so background color can be passed, for videos its fine to be in body
}: MediaInfoProps) => {
  const { getIcon, getLabel } = useGlobalContext();

  const hasVideoCopyright = !!copyright;
  const hasMobileDescription = !!(image_mobile && image_mobile?.description);
  const hasTabletDescription = !!(image_tablet && image_tablet?.description);
  const hasDesktopDescription = !!(image_desktop && image_desktop?.description);
  const hasFullhdDescription = !!(image_fullhd && image_fullhd?.description);
  const hasVideoDescription = !!(video && video?.description);

  const portalContainer = useRef<HTMLDivElement>(null);

  const copyrightJSX = generateCopyrightJSX(
    image_desktop || undefined,
    getLabel,
  );

  if (
    !trigger &&
    !hasMobileDescription &&
    !hasTabletDescription &&
    !hasDesktopDescription &&
    !hasFullhdDescription &&
    !hasVideoDescription &&
    !hasVideoCopyright &&
    copyrightJSX.length === 0
  ) {
    return null;
  }
  return (
    <>
      <ModalRoot>
        <ModalTrigger>
          {trigger ?? (
            <button
              className={`media-info-button ${styles["media-info-button"]} ${disableAbsolute ? styles["is-static"] : ""} button-reset`}
              aria-label={getLabel("aria_label_media_info_button")}
            >
              <CustomSVG
                src={getIcon("info")}
                wrapper={"span"}
                width={16}
                height={16}
                aria-hidden="true"
                className="is-flex"
              />
            </button>
          )}
        </ModalTrigger>

        <ModalPortal
          portalRef={modalInsideBody ? undefined : portalContainer.current}
        >
          <ModalOverlay />

          <ModalContent>
            <ModalClose />
            <MediaInfoModalContent
              copyrightJSX={!hasVideoCopyright ? copyrightJSX : []}
              copyrightAriaLabel={copyrightAriaLabel}
              media={media}
              image_desktop={image_desktop || undefined}
              image_fullhd={image_fullhd || undefined}
              image_tablet={image_tablet || undefined}
              image_mobile={image_mobile || undefined}
              hasVideoDescription={hasVideoDescription}
              hasMobileDescription={hasMobileDescription}
              hasTabletDescription={hasTabletDescription}
              hasDesktopDescription={hasDesktopDescription}
              hasFullhdDescription={hasFullhdDescription}
              video={video}
              hasVideoCopyright={hasVideoCopyright}
              copyright={copyright}
            />
          </ModalContent>
        </ModalPortal>
      </ModalRoot>
    </>
  );
};

export default MediaInfo;
